import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Grid2 as Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Add, Delete } from "@mui/icons-material";
import { TagManager } from "components/TagManager";
import { DomainColorSquares } from "components/Requirements";
import { Tag } from "types/tag";
import { Requirement } from "types/requirement";
import { EntitySelector, EntitySelectorWithTabs } from "modals/EntitySelector";
import { getFilteredList } from "utils/Array";
import {
  useAddAcademicYearMutation,
  useAddAcademicYearTagMutation,
  useGetAcademicYearsTemplateQuery,
  useAddAcademicYearRequirementMutation,
  useGetOrgRequirementsByOrgLazyQuery,
} from "graphql/__generated__/hooks";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { AcademicYear } from "types/academicYear";
import { getAcademicYearDates } from "utils/Datetime";
import { Messages, NotificationSeverity, REQUIREMENT } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import dayjs from "dayjs";
import { Organization } from "types/organization";
import { useClinicText } from "hooks/useClinicText";

type AcademicYearStepProps = {
  onAcademicYearCreated: (academicYearId: string) => void;
  onBack: () => void;
  academicYear?: AcademicYear;
  programId?: string | null;
  onNext: () => void;
  organization?: Organization;
};

export const AcademicYearStep: FC<AcademicYearStepProps> = ({
  onAcademicYearCreated,
  onBack,
  academicYear,
  programId,
  onNext,
  organization,
}) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [openSelectTemplates, setOpenSelectTemplates] = useState(false);
  const [openEntitySelector, setOpenEntitySelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AcademicYear | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const [addAcademicYear] = useAddAcademicYearMutation();
  const [addAcademicYearTag] = useAddAcademicYearTagMutation();
  const [addAcademicYearRequirement] = useAddAcademicYearRequirementMutation();
  const { data: templates } = useGetAcademicYearsTemplateQuery();
  const [getOrganizationRequirements, { data: requirementData }] =
    useGetOrgRequirementsByOrgLazyQuery();
  const { start_date, end_date } = getAcademicYearDates();
  const { notify } = useNotifications();
  const { textYearlyCurriculum } = useClinicText(false, organization);

  const reqList = useMemo(() => {
    if (requirementData) {
      return (
        (requirementData.organization_requirement.map(
          (req) => req.requirement,
        ) as Requirement[]) || []
      );
    }
    return [];
  }, [requirementData]);

  useEffect(() => {
    if (organization) {
      getOrganizationRequirements({
        variables: { orgId: organization.org_id },
      });
    }
  }, [organization]);

  const initialValues = {
    name: "",
    start_date: start_date,
    end_date: end_date,
  };

  const validationSchema = () =>
    Yup.object().shape({
      name: Yup.string().required("Program Name is required"),
      start_date: Yup.string().required("Start Date is required"),
      end_date: Yup.string().required("End Date is required"),
    });

  const handleAcademicYearAndTags = async (academicYearId: string) => {
    if (tags.length === 0) return;
    tags.forEach((tag) => {
      addAcademicYearTag({
        variables: {
          academicYearId: academicYearId,
          tagId: tag.tag_id,
        },
      });
    });
  };

  const handleRequirements = async (academicYearId: string) => {
    if (requirements.length === 0) return;
    const data = requirements.map((req) => ({
      academic_year_id: academicYearId,
      requirement_id: req.requirement_id,
    }));
    await addAcademicYearRequirement({ variables: { objects: data } });
  };

  const handleCreateAcademicYear = async (reqValues: AcademicYear) => {
    setLoading(true);
    try {
      const result = await addAcademicYear({
        variables: {
          ...reqValues,
          // note: program_id is not used in the onboarding
          program_id: programId,
        },
      });

      const academicYearId =
        result.data?.insert_academic_year?.returning &&
        result.data?.insert_academic_year?.returning[0]?.academic_year_id;

      if (academicYearId) {
        await handleAcademicYearAndTags(academicYearId);
        await handleRequirements(academicYearId);
        onAcademicYearCreated(academicYearId);
        notify({
          message: Messages.successAcademicYearCreated,
          severity: NotificationSeverity.success,
        });
        onNext();
      }
    } catch (error) {
      notify({
        message: "Error creating program",
        severity: NotificationSeverity.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAcademicYear = async (reqValues: AcademicYear) => {
    onNext();
  };

  useEffect(() => {
    if (selectedTemplate) {
      setTags(selectedTemplate.tags || []);
      setRequirements(selectedTemplate.requirements || []);

      formik.setValues({
        name: selectedTemplate.name,
        start_date,
        end_date,
      });
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (academicYear) {
      setTags(academicYear.tags || []);
      setRequirements(academicYear.requirements || []);

      formik.setValues({
        name: academicYear.name,
        start_date: academicYear.start_date || start_date,
        end_date: academicYear.end_date || end_date,
      });
    }
  }, [academicYear]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        name: values.name,
        start_date: values.start_date,
        end_date: values.end_date,
      };
      if (!academicYear) {
        handleCreateAcademicYear(reqValues as AcademicYear);
      } else {
        handleUpdateAcademicYear(reqValues as AcademicYear);
      }
    },
  });

  const handleAddTag = async (tag: Tag) => {
    setTags([...tags, tag]);
  };

  const handleDeleteTag = async (tagToRemove: Tag) => {
    const newTags = tags.filter((tag) => tag.tag_id !== tagToRemove.tag_id);
    setTags(newTags);
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 1 }}>
        {textYearlyCurriculum}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Configure your {textYearlyCurriculum} settings and timeline
      </Typography>

      {/* Program Templates */}
      <Box mb={4}>
        <Card variant="outlined" sx={{ bgcolor: "grey.50" }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Typography variant="h6">Program Templates</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Save time by using a pre-configured template with common academic
              periods, dates, and requirements.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setOpenSelectTemplates(true)}
              startIcon={<Add />}
            >
              Browse Templates
            </Button>
          </CardContent>
        </Card>
      </Box>

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3}>
              {/* Program Name */}
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  onChange={formik.handleChange}
                  fullWidth
                  name="name"
                  label="Program Name *"
                  type="text"
                  id="name"
                  value={formik?.values?.name}
                  size="small"
                  margin="dense"
                  placeholder="e.g., 2024-2025 Program"
                  helperText="This will be the display name for your program"
                />
                <ErrorMessage name="name" />
              </Grid>

              {/* Start Date y End Date */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Start Date *"
                  value={dayjs(formik?.values?.start_date)}
                  onChange={(newValue) => {
                    formik.setFieldValue(
                      "start_date",
                      newValue?.format("YYYY-MM-DD"),
                    );
                  }}
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      size: "small",
                      margin: "dense",
                    },
                  }}
                />
                <ErrorMessage name="start_date" />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="End Date *"
                  value={dayjs(formik?.values?.end_date)}
                  onChange={(newValue) => {
                    formik.setFieldValue(
                      "end_date",
                      newValue?.format("YYYY-MM-DD"),
                    );
                  }}
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      size: "small",
                      margin: "dense",
                    },
                  }}
                />
                <ErrorMessage name="end_date" />
              </Grid>

              {/* Academic Periods & Tags */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  Academic Periods & Tags
                </Typography>

                <TagManager
                  tags={tags}
                  onAdd={handleAddTag}
                  onDelete={handleDeleteTag}
                  isEditing={true}
                />
              </Grid>

              {/* Requirements Section */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  EPA Requirements
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Select EPA requirements that will be included in this academic
                  year.
                </Typography>

                <Stack spacing={2}>
                  {requirements.length === 0 && (
                    <Typography variant="body2" align="center" sx={{ py: 2 }}>
                      No requirements added yet. Add EPA requirements for this
                      program.
                    </Typography>
                  )}
                  {requirements.length > 0 && (
                    <List>
                      {requirements.map((requirement, i) => (
                        <ListItem
                          key={requirement.requirement_id}
                          sx={{
                            borderBottom:
                              i === requirements.length - 1
                                ? "none"
                                : "1px solid rgba(0,0,0,0.2)",
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => {
                                setRequirements(
                                  requirements.filter(
                                    (data) =>
                                      data.requirement_id !==
                                      requirement.requirement_id,
                                  ),
                                );
                              }}
                            >
                              <Delete />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={requirement.name}
                            secondary={
                              <Stack spacing={0.5} alignItems="flex-start">
                                {requirement.description?.slice(0, 120) ? (
                                  <span>
                                    {requirement.description.slice(0, 120)}
                                  </span>
                                ) : null}
                                <DomainColorSquares requirement={requirement} />
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Button
                    variant="outlined"
                    onClick={() => {
                      setOpenEntitySelector(true);
                    }}
                    startIcon={<Add />}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Add EPA Requirement
                  </Button>
                </Stack>
              </Grid>

              {/* Botones de navegación */}
              <Grid size={12}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="outlined"
                    onClick={onBack}
                    sx={{ minWidth: 100 }}
                  >
                    Back
                  </Button>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={onNext}
                      disabled={loading}
                    >
                      Skip this step and continue
                    </Button>
                    <Button
                      disabled={loading}
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: 150 }}
                    >
                      Next
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </form>
      </FormikProvider>

      {/* Requirements Selector Modal */}
      <EntitySelectorWithTabs
        open={openEntitySelector}
        onClose={() => setOpenEntitySelector(false)}
        title={"Select Requirements"}
        listEntities={getFilteredList(reqList, requirements, "requirement_id")}
        filterCriteria={(search: string, data: any) =>
          data.name.toLowerCase().includes(search.toLowerCase())
        }
        onSelect={(selected: any) => {
          const toAdd = Array.isArray(selected) ? selected : [selected];
          setRequirements([...requirements, ...toAdd]);
        }}
        displayField="name"
        keyField="requirement_id"
        multiSelect
        tabs={[
          {
            label: "EPA",
            filterTabCriteria: (requirement: any) =>
              requirement?.type === REQUIREMENT.REQUIREMENT_EPA,
          },
          {
            label: "Competency",
            filterTabCriteria: (requirement: any) =>
              requirement?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
          },
        ]}
      />

      {/* Template Selector Modal */}
      {openSelectTemplates && (
        <EntitySelector
          open={openSelectTemplates}
          onClose={() => setOpenSelectTemplates(false)}
          title={"Select Program Template"}
          listEntities={templates?.academic_year || []}
          filterCriteria={(search: string, data: any) =>
            data.name.toLowerCase().includes(search.toLowerCase())
          }
          onSelect={(selected: any) => {
            setSelectedTemplate({
              ...selected,
              tags: selected.tags?.map((tag: any) => tag.tag),
              requirements: selected.requirements?.map(
                (req: any) => req.requirement,
              ),
            });
            setOpenSelectTemplates(false);
          }}
          displayField="name"
          keyField="academic_year_id"
        />
      )}
    </Box>
  );
};
